/**
 * Wipe a user's content and leave them with an empty account.
 *
 * A port of taskdojo-app/src/services/resetService.js. Keep the two in step:
 * both platforms hit the same Firestore project, so a collection this misses is
 * data that survives a wipe the user asked for.
 *
 * Not the same as deleting the account: the sign-in survives, so the user keeps
 * their email, password and connections list and simply starts from nothing.
 * Everything the score is computed from goes, so the belt resets to White.
 *
 * Deliberately explicit about what it touches. A `deleteEverything()` that
 * globbed collections would quietly stop covering new ones as they are added,
 * and the failure mode is data surviving a wipe. Adding a collection to the app
 * means adding it here.
 *
 * It is resumable in the sense that a failure part-way leaves the rest
 * deletable on a retry; it is not transactional, and cannot be, because
 * Firestore has no cross-collection transaction at this size.
 */
import {
  collection, query, where, getDocs, deleteDoc, doc, writeBatch, setDoc,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

/** Top-level collections, and the field that ties a document to its owner. */
const OWNED: Array<{ name: string; field: string }> = [
  { name: 'tasks', field: 'createdBy' },
  { name: 'habits', field: 'userId' },
  { name: 'notes', field: 'userId' },
  { name: 'ganttCharts', field: 'userId' },
  { name: 'budgetCategories', field: 'userId' },
  { name: 'budgetEntries', field: 'userId' },
];

/** Subcollections under users/{uid}. */
const SUBCOLLECTIONS = ['completions', 'feelings', 'chatMessages', 'dailyReports'];

const BATCH = 400; // Firestore caps a write batch at 500.

async function deleteQuery(q: any): Promise<number> {
  const snap = await getDocs(q);
  if (snap.empty) return 0;

  let removed = 0;
  let batch = writeBatch(db);
  let n = 0;

  for (const d of snap.docs) {
    batch.delete(d.ref);
    n += 1;
    removed += 1;
    if (n >= BATCH) {
      await batch.commit();
      batch = writeBatch(db);
      n = 0;
    }
  }
  if (n > 0) await batch.commit();
  return removed;
}

export interface ResetResult {
  counts: Record<string, number>;
  failures: Array<{ name: string; message: string }>;
}

/**
 * @param uid     the signed-in user
 * @param onStep  optional progress callback, given the collection being cleared
 */
export async function clearAllUserData(
  uid: string,
  onStep?: (label: string) => void,
): Promise<ResetResult> {
  if (!uid) throw new Error('Not signed in.');

  const counts: Record<string, number> = {};
  const failures: Array<{ name: string; message: string }> = [];

  for (const { name, field } of OWNED) {
    try {
      onStep?.(name);
      counts[name] = await deleteQuery(query(collection(db, name), where(field, '==', uid)));
    } catch (error: any) {
      failures.push({ name, message: error?.message || String(error) });
    }
  }

  // Lists are shared, so leaving one is not the same as deleting it. Only lists
  // this user owns are removed; on shared ones they are taken out of members,
  // which is what leaving a shared list means.
  try {
    onStep?.('lists');
    // Queried by membership, not ownership. The read rule on lists is
    // `uid in members`, and Firestore rejects a query whose filter does not
    // guarantee every match is readable -- a where('owner','==',uid) query is
    // refused outright, before it reads anything.
    const mine = await getDocs(
      query(collection(db, 'lists'), where('members', 'array-contains', uid)),
    );
    let removed = 0;
    for (const d of mine.docs) {
      if (d.data().owner === uid) {
        await deleteDoc(d.ref);
        removed += 1;
      } else {
        // Someone else's list: leave it, don't delete it out from under them.
        const members = (d.data().members || []).filter((m: string) => m !== uid);
        await setDoc(d.ref, { members }, { merge: true });
      }
    }
    counts.lists = removed;
  } catch (error: any) {
    failures.push({ name: 'lists', message: error?.message || String(error) });
  }

  // Tasks you only watch are not yours to delete, and a watched task cannot be
  // completed or deleted either, so it would sit on the list looking permanent.
  // Take the user out of the watchers array instead -- the one edit the rules
  // allow a watcher to make.
  try {
    onStep?.('watched tasks');
    const watched = await getDocs(
      query(collection(db, 'tasks'), where('watchers', 'array-contains', uid)),
    );
    let left = 0;
    for (const d of watched.docs) {
      if (d.data().createdBy === uid) continue; // already deleted above
      const watchers = (d.data().watchers || []).filter((w: string) => w !== uid);
      await setDoc(d.ref, { watchers }, { merge: true });
      left += 1;
    }
    counts.watched = left;
  } catch (error: any) {
    failures.push({ name: 'watched tasks', message: error?.message || String(error) });
  }

  // Tasks assigned to you by someone else: same problem, same shape of answer.
  try {
    onStep?.('assigned tasks');
    const assigned = await getDocs(
      query(collection(db, 'tasks'), where('assignedTo', '==', uid)),
    );
    let cleared = 0;
    for (const d of assigned.docs) {
      if (d.data().createdBy === uid) continue;
      await setDoc(d.ref, { assignedTo: '' }, { merge: true });
      cleared += 1;
    }
    counts.assigned = cleared;
  } catch (error: any) {
    failures.push({ name: 'assigned tasks', message: error?.message || String(error) });
  }

  for (const sub of SUBCOLLECTIONS) {
    try {
      onStep?.(sub);
      counts[sub] = await deleteQuery(collection(db, 'users', uid, sub));
    } catch (error: any) {
      failures.push({ name: sub, message: error?.message || String(error) });
    }
  }

  // Reset the counters kept on the profile itself. The document stays: it is
  // the account, and blanking it would sign the user into a broken state.
  try {
    await setDoc(doc(db, 'users', uid), {
      currentStreak: 0,
      bestStreak: 0,
      totalPoints: 0,
      updatedAt: new Date().toISOString(),
    }, { merge: true });
  } catch (error: any) {
    failures.push({ name: 'profile', message: error?.message || String(error) });
  }

  return { counts, failures };
}

/** Local caches that would otherwise survive the wipe and look like ghosts. */
export const LOCAL_KEYS_TO_CLEAR = [
  'ganttSortBy',
  'ganttShowWatching',
  'ganttShowHabits',
  'ganttSelectedCharts',
  '@taskdojo_celebrated_belt_index',
];
